import { format } from 'date-fns/format'
import { Observer, observer } from 'mobx-react-lite'
import { FC, ReactNode, SVGProps, useMemo } from 'react'
import VisitCardsExpansion from 'singletons/VisitCardsExpansion'
import ChevronDownIcon from '@ui-components-3/icons/lib/regular/ChevronDown'
import CalendarIcon from '@ui-components-3/icons/lib/regular/Calendar'
import ClockIcon from '@ui-components-3/icons/lib/regular/Clock'
import UserGroupIcon from '@ui-components-3/icons/lib/regular/UserGroup'

type HeaderValueProps = { value: ReactNode; Icon: FC<SVGProps<SVGSVGElement>> }

const HeaderValue = ({ Icon, value }: HeaderValueProps) => {
  if (!value) {
    return null
  }

  return (
    <span className="inline-flex items-center gap-1 typography-body-l text-neutral-800 font-medium">
      {!!Icon && (
        <span className="w-6 h-6 p-1">
          <Icon aria-hidden="true" className="w-full h-full text-primary-600" />
        </span>
      )}
      {value}
    </span>
  )
}

const getDisplayVisitType = (visitType: string, scheduledType: string) => {
  const isNow = scheduledType === 'now'

  switch (visitType) {
    case 'therapy':
      return isNow ? 'TalkNow' : 'Scheduled Counseling'
    case 'medical':
      return `Medical${isNow ? ' Now' : ''}`
    case 'hc':
      return 'Health Coaching'
    case 'success_coaching':
      return `Success Coaching${isNow ? ' - On Demand' : ''}`
    default:
      return visitType
  }
}

type VisitCardHeaderProps = {
  // TODO - improve visit type
  visit: any
  memberMode?: boolean
}

const VisitCardHeader = ({ visit, memberMode }: VisitCardHeaderProps) => {
  const { scheduledAt, visitType, scheduledType } = visit || {}

  const displayDate = useMemo(() => format(new Date(scheduledAt), 'MMM do yyyy'), [scheduledAt])
  const displayTime = useMemo(() => format(new Date(scheduledAt), 'hh:mm a').toLowerCase(), [scheduledAt])
  const displayVisitType = useMemo(
    () => <span className="capitalize">{getDisplayVisitType(visitType, scheduledType)}</span>,
    [scheduledType, visitType],
  )

  return (
    <div className="flex justify-between gap-4 flex-wrap">
      <div className="flex-1 flex flex-col gap-2 min-w-[300px]">
        <h5 className="typography-h5 text-primary-600">
          <Observer>{() => (memberMode ? visit.memberFullName : visit.providerName)}</Observer>
        </h5>
        <div className="flex items-center justify-start flex-wrap gap-x-4 gap-y-1">
          <HeaderValue Icon={CalendarIcon} value={displayDate} />
          <HeaderValue Icon={ClockIcon} value={displayTime} />
          <HeaderValue Icon={UserGroupIcon} value={displayVisitType} />
        </div>
      </div>

      <Observer>
        {() => {
          const expanded = VisitCardsExpansion.expanded(visit.pendingVisitId)

          return (
            <button
              className="btn-md btn-primary"
              onClick={() => {
                VisitCardsExpansion.toggle(visit.pendingVisitId)
                visit.checkIfExtendedRecordExists()
              }}
              type="button"
            >
              {expanded ? 'Hide details' : 'View details'}
              <ChevronDownIcon
                style={{
                  transform: `rotate(${expanded ? 180 : 0}deg)`,
                }}
                aria-hidden="true"
              />
            </button>
          )
        }}
      </Observer>
    </div>
  )
}

export default observer(VisitCardHeader)
